import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PenaltyReminderSettings } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';

export function PenaltyReminderSettingsButton({ agencyId, scope = 'agency' }: { agencyId: number; scope?: string }) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState(scope);
  const [draft, setDraft] = useState<PenaltyReminderSettings | null>(null);
  const key = ['penalty-reminder-settings', agencyId, selectedScope];
  const path = `booking-follow-up/agencies/${agencyId}/settings`;
  const query = useQuery(
    key,
    async () => (await client.get<PenaltyReminderSettings>(path, { params: { scope: selectedScope } })).data,
    { enabled: open, retry: false },
  );
  const scopes = useQuery(
    ['penalty-reminder-connections', agencyId],
    async () =>
      (await client.get<{ scope: string; name: string }[]>(`booking-follow-up/agencies/${agencyId}/connections`)).data,
    { enabled: open && scope === 'agency', retry: false },
  );
  const current = draft ?? query.data;
  const save = useMutation(async () => client.put(path, current, { params: { scope: selectedScope } }), {
    onSuccess: async () => {
      await cache.invalidateQueries(key);
      setOpen(false);
      setDraft(null);
    },
  });
  return (
    <>
      <Button
        onClick={() => {
          setSelectedScope(scope);
          setDraft(null);
          save.reset();
          setOpen(true);
        }}
      >
        {t('penaltyReminders.title')}
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm" onClose={() => !save.isLoading && setOpen(false)}>
        <DialogTitle>{t('penaltyReminders.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Typography>{t('penaltyReminders.hint')}</Typography>
            {scope === 'agency' && (
              <TextField
                select
                label={t('penaltyReminders.scope')}
                value={selectedScope}
                disabled={save.isLoading}
                onChange={(e) => {
                  setSelectedScope(e.target.value);
                  setDraft(null);
                  save.reset();
                }}
              >
                <MenuItem value="agency">{t('penaltyReminders.agency')}</MenuItem>
                {(scopes.data ?? []).map((c) => (
                  <MenuItem key={c.scope} value={c.scope}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {(query.isError || save.isError || scopes.isError) && (
              <Alert severity="error">{t('penaltyReminders.error')}</Alert>
            )}
            {current && !query.isError && (
              <>
                <TextField
                  select
                  label={t('penaltyReminders.title')}
                  value={current.enabled === null ? 'inherit' : String(current.enabled)}
                  disabled={save.isLoading || query.isFetching}
                  onChange={(e) =>
                    setDraft({ ...current, enabled: e.target.value === 'inherit' ? null : e.target.value === 'true' })
                  }
                >
                  {selectedScope !== 'agency' && <MenuItem value="inherit">{t('penaltyReminders.inherit')}</MenuItem>}
                  <MenuItem value="true">{t('penaltyReminders.enabled')}</MenuItem>
                  <MenuItem value="false">{t('penaltyReminders.disabled')}</MenuItem>
                </TextField>
                {current.enabled === true && (
                  <>
                    <TextField
                      label={t('penaltyReminders.days')}
                      type="number"
                      value={current.daysBefore}
                      inputProps={{ min: 0, max: 365, step: 1 }}
                      sx={{ '& input': { fontSize: 16 } }}
                      disabled={save.isLoading || current.enabled !== true}
                      onChange={(e) =>
                        setDraft({ ...current, daysBefore: e.target.value === '' ? NaN : Number(e.target.value) })
                      }
                    />
                    <TextField
                      label={t('penaltyReminders.timezone')}
                      value={current.timezone}
                      placeholder="America/Montevideo"
                      sx={{ '& input': { fontSize: 16 } }}
                      disabled={save.isLoading || current.enabled !== true}
                      onChange={(e) => setDraft({ ...current, timezone: e.target.value })}
                    />
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setOpen(false)}>
            {t('penaltyReminders.close')}
          </Button>
          <Button
            disabled={
              !current ||
              query.isFetching ||
              query.isError ||
              save.isLoading ||
              !Number.isInteger(current.daysBefore) ||
              current.daysBefore < 0 ||
              current.daysBefore > 365
            }
            onClick={() => save.mutate()}
          >
            {t('penaltyReminders.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
